import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '~/utils';
import React from 'react';

export const buttonVariants = cva(
    "aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap font-medium outline-none transition-all focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-60 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
    {
        variants: {
            variant: {
                default:
                    'shadow-gray-900/5 border border-secondary bg-secondary text-white shadow-sm hover:bg-secondary/90 focus-visible:ring-secondary active:scale-[98%] disabled:border-secondary/30 disabled:bg-secondary/30 disabled:hover:bg-secondary/30',
                destructive:
                    'shadow-gray-900/5 border border-destructive bg-destructive text-white shadow-sm hover:bg-destructive/90 focus-visible:ring-destructive/50 active:scale-[98%] disabled:border-destructive/30 disabled:bg-destructive/30 disabled:hover:bg-destructive/30',
                outline:
                    'shadow-gray-900/5 border border-input bg-background text-foreground shadow-sm hover:border-secondary hover:bg-accent active:scale-[98%] disabled:border-input disabled:text-muted-foreground',
                secondary:
                    'shadow-gray-900/5 bg-secondary text-white shadow-sm hover:bg-secondary/90 active:scale-[98%] disabled:opacity-60 disabled:hover:bg-secondary',
                ghost: 'text-primary hover:bg-accent hover:text-accent-foreground active:scale-[97%] disabled:opacity-60',
                link: 'text-primary underline-offset-4 hover:underline disabled:text-muted-foreground disabled:no-underline',
                opaque: 'shadow-gray-900/5 bg-white/25 text-white shadow-sm hover:bg-white/40 active:scale-[98%] disabled:opacity-60 disabled:hover:bg-white/25',
            },
            size: {
                xs: 'h-7 gap-1 rounded-2xl px-2.5 py-1 text-xs active:scale-[97%]',
                sm: 'h-8 gap-1 rounded-3xl px-3 py-2 text-sm active:scale-[97%]',
                default: 'h-10 gap-1.5 rounded-3xl px-4 py-2.5 text-sm',
                lg: 'h-12 gap-2 rounded-4xl px-6 py-3 text-base',
                xl: 'h-14 gap-3 rounded-4xl px-8 py-4 text-base',
                icon: 'size-9 rounded-2xl',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    },
);

interface ButtonProps extends React.ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
    asChild?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
    className,
    variant,
    size,
    asChild = false,
    ...props
}) => {
    const Comp = asChild ? Slot : 'button';

    return (
        <Comp
            data-slot="button"
            className={cn(buttonVariants({ variant, size, className }))}
            {...props}
        />
    );
};
